import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  LinearProgress,
  Chip,
  IconButton,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import FlipIcon from '@mui/icons-material/Flip';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckIcon from '@mui/icons-material/Check';

interface FlashcardContent {
  id: number;
  front: string;
  back: string;
  category?: string;
}

interface FlashcardSessionProps {
  content: FlashcardContent[];
  onComplete: (results: any) => void;
  onProgressUpdate: (progress: number) => void;
}

const FlashcardContainer = styled(Card)(({ theme }) => ({
  minHeight: 300,
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
  border: `2px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const FlashcardSession: React.FC<FlashcardSessionProps> = ({ 
  content, 
  onComplete, 
  onProgressUpdate 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [confidence, setConfidence] = useState<{ [key: number]: 'easy' | 'hard' }>({});
  const [sessionComplete, setSessionComplete] = useState(false);

  // Mock flashcard data if none provided
  const flashcards = content.length > 0 ? content : [
    { id: 1, front: 'Comprehensive', back: 'Complete and thorough; including all or nearly all elements' },
    { id: 2, front: 'Collaborate', back: 'Work jointly on an activity or project' },
    { id: 3, front: 'Articulate', back: 'Express ideas clearly and effectively' },
  ];

  const currentCard = flashcards[currentIndex];
  const totalCards = flashcards.length;
  const progress = ((currentIndex + 1) / totalCards) * 100;

  useEffect(() => {
    onProgressUpdate(progress);
  }, [progress, onProgressUpdate]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleConfidence = (level: 'easy' | 'hard') => {
    setConfidence(prev => ({
      ...prev,
      [currentCard.id]: level
    }));
    
    // Auto advance to next card
    setTimeout(() => {
      if (currentIndex < totalCards - 1) {
        setCurrentIndex(prev => prev + 1);
        setIsFlipped(false);
      } else {
        handleComplete();
      }
    }, 500);
  };

  const handleNext = () => {
    if (currentIndex < totalCards - 1) {
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setIsFlipped(false);
    }
  };

  const handleComplete = () => {
    const easyCards = Object.values(confidence).filter(c => c === 'easy').length;
    const hardCards = Object.values(confidence).filter(c => c === 'hard').length;
    const reviewedCards = Object.keys(confidence).length;
    
    const results = {
      totalCards: totalCards,
      reviewedCards: reviewedCards,
      easyCards: easyCards,
      hardCards: hardCards,
      completionRate: (reviewedCards / totalCards) * 100,
      completedAt: new Date(),
    };

    setSessionComplete(true);
    onProgressUpdate(100);
    onComplete(results);
  };

  if (sessionComplete) {
    const reviewedCards = Object.keys(confidence).length;
    const completionRate = Math.round((reviewedCards / totalCards) * 100);

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, color: 'primary.main' }}>
            Flashcard Session Complete! ✨
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h2" component="div" sx={{ mb: 1, fontWeight: 'bold', color: 'success.main' }}>
              {completionRate}%
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You reviewed {reviewedCards} out of {totalCards} flashcards
            </Typography>
          </Box>

          <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {Object.values(confidence).filter(c => c === 'easy').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Easy Cards
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                {Object.values(confidence).filter(c => c === 'hard').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Need Review
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            size="large"
            onClick={() => onComplete({ completionRate, reviewedCards, totalCards })}
            sx={{ mt: 3, px: 4 }}
          >
            Continue to My Classroom
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentConfidence = confidence[currentCard.id];
  const isLastCard = currentIndex === totalCards - 1;

  return (
    <Box>
      {/* Progress Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            Card {currentIndex + 1} of {totalCards}
          </Typography>
          <Chip 
            label={`${Math.round(progress)}% Complete`} 
            color="primary" 
            variant="outlined" 
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Flashcard */}
      <FlashcardContainer onClick={handleFlip} sx={{ mb: 3 }}>
        <CardContent sx={{ 
          height: 300, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: isFlipped ? 'primary.light' + '10' : 'background.paper',
        }}>
          <Fade in={!isFlipped} timeout={300}>
            <Box sx={{ 
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: isFlipped ? 'none' : 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: 3,
            }}>
              <Typography variant="h3" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
                {currentCard.front}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 'auto', mb: 2 }}>
                <FlipIcon color="action" />
                <Typography variant="body2" color="text.secondary">
                  Click to flip
                </Typography>
              </Stack>
            </Box>
          </Fade>

          <Fade in={isFlipped} timeout={300}>
            <Box sx={{ 
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: isFlipped ? 'flex' : 'none',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: 3,
            }}>
              <Typography variant="h5" component="div" sx={{ mb: 2, fontWeight: 'medium' }}>
                {currentCard.back}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto', mb: 2 }}>
                How well did you know this?
              </Typography>
            </Box>
          </Fade>
        </CardContent>
      </FlashcardContainer>

      {/* Confidence Buttons (shown when flipped) */}
      {isFlipped && (
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<ThumbDownIcon />}
            onClick={() => handleConfidence('hard')}
            size="large"
            sx={{ px: 4 }}
          >
            Need More Practice
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<ThumbUpIcon />}
            onClick={() => handleConfidence('easy')}
            size="large"
            sx={{ px: 4 }}
          >
            Got It!
          </Button>
        </Stack>
      )}

      {/* Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={handlePrevious}
          disabled={currentIndex === 0}
        >
          Previous
        </Button>

        <Typography variant="body2" color="text.secondary">
          {Object.keys(confidence).length} of {totalCards} reviewed
        </Typography>

        {isLastCard ? (
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={handleComplete}
            color="success"
          >
            Finish Session
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<NavigateNextIcon />}
            onClick={handleNext}
          >
            Next Card
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default FlashcardSession;